import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { Button, Loader, Modal } from 'ncoded-component-library';
import { Field, Form } from 'react-final-form';
import { ObjectValues } from 'types';
import TextAreaField from 'components/TextAreaField';
import { SOFT_SIGNALS, SoftSignal, SoftSignalPayload } from 'models/Lead';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { SoftSignalInitiatedEventProps } from 'models/MixpanelEvents';
import mixpanelService from 'services/mixpanelService';

import './DislikeSignalModal.styles.scss';

const OPTIONS = {
  STAY: 'stay',
  WITHDRAW: 'withdraw',
} as const;

type Option = ObjectValues<typeof OPTIONS>;

type DislikeSignalModalProps = {
  className?: string;
  dataRoomNodeId: string;
  dataRoomId: string;
  dealRoomId: string;
  onSuccess: (lastSoftSignal: SoftSignal, withdrawInterest?: boolean) => void;
};

const DislikeSignalModal: React.ForwardRefRenderFunction<
  ModalRef,
  DislikeSignalModalProps
> = (props, ref) => {
  const {
    className,
    dataRoomNodeId,
    dataRoomId,
    dealRoomId,
    onSuccess,
    ...rest
  } = props;

  const [choice, setChoice] = useState<Option>();
  const [isSkipPressed, setIsSkipPressed] = useState(false);

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('dislike-signal-modal', className);

  const { t } = useTranslation();
  const getMixpanelProps = useGetMixpanelProps();

  const resetState = useCallback(() => setChoice(null), []);

  const onSubmit = useCallback(
    async ({ message }: SoftSignalPayload) => {
      const softSignal = SOFT_SIGNALS.DISLIKE;
      const withdrawInterest = choice === OPTIONS.WITHDRAW;

      try {
        mixpanelService.softSignalInitiated(
          getMixpanelProps({
            dataRoomId,
            documentId: dataRoomNodeId,
            dealId: dealRoomId,
            reaction: t('notInterested'),
          }) as SoftSignalInitiatedEventProps,
        );

        await api.dataRoom.nodes.softSignals(dataRoomNodeId, {
          softSignal,
          withdrawInterest,
          message: isSkipPressed ? undefined : message,
        });

        onSuccess(softSignal, withdrawInterest);

        if (!withdrawInterest) {
          popSuccess(t('softSignalSuccessMessage'));
        }

        modal?.close();
      } catch (e) {
        popServerError(e);
      }
    },
    [
      choice,
      dataRoomNodeId,
      isSkipPressed,
      modal,
      dataRoomId,
      dealRoomId,
      getMixpanelProps,
      onSuccess,
      popServerError,
      popSuccess,
      t,
    ],
  );

  return (
    <Modal
      ref={modalRef}
      onClose={resetState}
      className={classes}
      title={t(
        !choice
          ? 'withdrawInterestQuestion'
          : choice === OPTIONS.STAY
          ? 'interestKept'
          : 'interestWithdrawn',
      )}
      {...rest}
    >
      <Form
        onSubmit={onSubmit}
        render={({ submitting, handleSubmit }) => (
          <>
            {submitting && <Loader />}

            {!choice && (
              <div>
                <p>{t('withdrawInterestSubquestion')}</p>
                <div className="actions">
                  <Button
                    variant="link"
                    type="button"
                    onClick={() => setChoice(OPTIONS.STAY)}
                  >
                    {t('illStay')}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setChoice(OPTIONS.WITHDRAW)}
                  >
                    {t('withdraw')}
                  </Button>
                </div>
              </div>
            )}

            {choice && (
              <form onSubmit={handleSubmit}>
                <Field
                  name="message"
                  component={TextAreaField}
                  label={t(
                    choice === OPTIONS.STAY
                      ? 'tellUsYourThoughts'
                      : 'tellUsWhyAreYouWithdrawing',
                  )}
                  rows={4}
                />

                <div className="actions">
                  <Button
                    variant="link"
                    disabled={submitting}
                    onClick={() => {
                      setIsSkipPressed(true);
                      setTimeout(handleSubmit, 100);
                    }}
                  >
                    {t('skip')}
                  </Button>

                  <Button
                    disabled={submitting}
                    onClick={() => {
                      setIsSkipPressed(false);
                      setTimeout(handleSubmit, 100);
                    }}
                  >
                    {t('submit')}
                  </Button>
                </div>
              </form>
            )}
          </>
        )}
      />
    </Modal>
  );
};

export default forwardRef(DislikeSignalModal);
